/**
 * HTML sanitization for any text we render via dangerouslySetInnerHTML.
 *
 * DOMPurify with a conservative profile: drops <script>, on* event handlers,
 * javascript: URIs, <iframe>, <object>, <embed>, <form>, etc. Allows the
 * common formatting tags + safe links, which covers the rich-text editors
 * used for patient notes and the HTML email bodies surfaced in the
 * communication center.
 *
 * Do NOT use this for app-controlled content (CMS, help search snippets) —
 * those should be controlled at source. Use this for ANY content that
 * originated from a user (rich-text editor save, inbound email body_html).
 */
import DOMPurify from 'dompurify';

const CONFIG: DOMPurify.Config = {
  // Whitelist common formatting + structural tags. Reject media, scripts,
  // forms, frames, etc.
  ALLOWED_TAGS: [
    'a', 'b', 'i', 'u', 'em', 'strong', 'p', 'br', 'span', 'div',
    'ul', 'ol', 'li', 'blockquote', 'pre', 'code',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
    'hr', 'img',
  ],
  ALLOWED_ATTR: ['href', 'title', 'target', 'rel', 'src', 'alt', 'style', 'class'],
  // Drop anything outside the allow-list completely (instead of leaving the
  // tag bare). Safer default for security-sensitive contexts.
  KEEP_CONTENT: true,
  // Force every external link to open noopener/noreferrer so a sanitized
  // <a> can't tabnap the user's session.
  ADD_ATTR: ['rel', 'target'],
  // Block dangerous URI schemes — DOMPurify's default already does this,
  // but we list them explicitly so future maintainers see the policy.
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel|cid):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
};

export function sanitizeHtml(dirty: string | null | undefined): string {
  if (!dirty) return '';
  // Cast to string to defang any non-string inputs (numbers, booleans).
  const out = DOMPurify.sanitize(String(dirty), CONFIG);
  return typeof out === 'string' ? out : String(out);
}

/** Wrap output in a __html object suitable for React's
 * dangerouslySetInnerHTML prop, so the call-site reads the same way as
 * before but only ever passes sanitized content. */
export function safeInnerHtml(dirty: string | null | undefined): { __html: string } {
  return { __html: sanitizeHtml(dirty) };
}
